import axios from "axios";

/**
 * Contact endpoint resolver.
 *
 * Production: `REACT_APP_CONTACT_ENDPOINT` MUST be set in the Cloudflare
 * Pages environment (e.g. `https://api.bnmgroup.it/contact`). This points
 * at the Cloudflare Worker in `/workers/contact/`.
 *
 * Development: if the env var is unset, we fall back to the local mocked
 * FastAPI mounted at `${REACT_APP_BACKEND_URL}/api/contact`, so the dev
 * workflow keeps working unchanged.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CONTACT_ENDPOINT =
  process.env.REACT_APP_CONTACT_ENDPOINT || `${BACKEND_URL}/api/contact`;

// Legacy export kept for any tests/tools that still reference it.
export const API_BASE = `${BACKEND_URL}/api`;

/**
 * Contact form payload — provider-independent.
 *
 * The contact endpoint (Cloudflare Worker in prod, mocked FastAPI in dev)
 * is a thin dispatcher. The concrete downstream provider (CRM, n8n,
 * email, ERP, webhook) is decided later and swapped without touching
 * this interface.
 */
export interface ContactPayload {
  name: string;
  company: string;
  email: string;
  phone?: string;
  country?: string;
  interest?: string;
  message: string;
  consent: boolean;
  locale: string;
  /** Honeypot — must remain empty. Filled only by spam bots. */
  website?: string;
}

export interface ContactResponse {
  ok: boolean;
  reference: string;
  received_at: string;
}

export async function submitContact(payload: ContactPayload): Promise<ContactResponse> {
  const { data } = await axios.post<ContactResponse>(CONTACT_ENDPOINT, payload, {
    timeout: 15000,
    headers: { "Content-Type": "application/json" },
  });
  return data;
}
